import type Echo from 'laravel-echo'
import { readonly, ref } from 'vue'

import { authoriseChannel } from '@/api/live'
import { reportError } from '@/observability'

/**
 * The console's one websocket connection (MRS 6.15, D125 to D128).
 *
 * The socket is a doorbell. A signal carries its name and a tenant id and no
 * record data: a view that hears one loads its data again through the API, so
 * nothing here is trusted and nothing here is shown. Views do not call this
 * module; they call `useLiveRefresh`.
 *
 * With no `VITE_WS_URL` there is no connection and the socket packages are
 * never fetched: the console behaves as it did before live updates, on its
 * polls and its reloads.
 */

export type LiveEcho = Echo<'pusher'>

type SignalHandler = () => void

interface LiveConfig {
  key: string
  host: string
  port: number
  tls: boolean
}

interface Connection {
  echo: LiveEcho
  /** How many listeners each channel has, so the last one out leaves it. */
  listeners: Map<string, number>
  close: () => void
}

/**
 * Sockudo advertises 120 s and pusher-js takes the smaller value, so a ping
 * goes out every 60 s and a proxy never sees the websocket as idle.
 */
const ACTIVITY_TIMEOUT_MS = 60_000

const reconnectListeners = new Set<SignalHandler>()

const connected = ref(false)

/**
 * Whether the session's socket is up now. A view never needs it to show data;
 * a poll that the signals make a fallback reads it to step back while signals
 * can arrive, and to step forward again the moment they cannot.
 */
export const liveConnected = readonly(connected)

let connection: Connection | null = null
let building: Promise<Connection | null> | null = null
/** Bumped by every disconnect, so a build that finishes late knows it is stale. */
let generation = 0

/** Reads a `wss://host[:port]` or `ws://host[:port]` address; null for anything else. */
function parseAddress(address: string): Omit<LiveConfig, 'key'> | null {
  if (!URL.canParse(address)) return null
  const url = new URL(address)
  if (url.protocol !== 'wss:' && url.protocol !== 'ws:') return null

  const tls = url.protocol === 'wss:'
  const defaultPort = tls ? 443 : 80
  return { host: url.hostname, port: url.port === '' ? defaultPort : Number(url.port), tls }
}

/** A deployment mistake: seen in the error reports, and the console carries on without a socket. */
function refuse(reason: string): null {
  reportError(new Error(`${reason}; live updates are off.`))
  return null
}

/**
 * Reads the websocket settings. Null with no address, which is "live updates
 * are off" and says nothing. An address that cannot be used is refused aloud.
 */
function liveConfig(): LiveConfig | null {
  const address = import.meta.env.VITE_WS_URL ?? ''
  if (address === '') return null

  const target = parseAddress(address)
  if (target === null) return refuse('VITE_WS_URL must be a wss:// or ws:// address')

  const key = import.meta.env.VITE_WS_KEY ?? ''
  if (key === '') return refuse('VITE_WS_URL is set without VITE_WS_KEY')

  return { key, ...target }
}

async function build(config: LiveConfig): Promise<Connection> {
  const { Echo: EchoClient, Pusher } = await import('./vendor')

  const echo: LiveEcho = new EchoClient({
    broadcaster: 'pusher',
    key: config.key,
    Pusher,
    cluster: '',
    wsHost: config.host,
    wsPort: config.port,
    wssPort: config.port,
    forceTLS: config.tls,
    // Websockets or nothing: the fallbacks are HTTP paths Sockudo does not serve.
    enabledTransports: ['ws', 'wss'],
    disableStats: true,
    activityTimeout: ACTIVITY_TIMEOUT_MS,
    // The console calls the API through openapi-fetch, which Echo cannot hook.
    withoutInterceptors: true,
    channelAuthorization: {
      customHandler: ({ socketId, channelName }, callback) => {
        authoriseChannel(socketId, channelName).then(
          (data) => callback(null, { auth: data.auth }),
          // A refusal is pusher-js's to handle: the channel stays unjoined
          // and the view keeps its polls. Nothing is shown for it.
          (error: unknown) =>
            callback(error instanceof Error ? error : new Error('authorisation failed'), null),
        )
      },
    },
  })

  // Echo reports one change through several pusher-js events, so the state is
  // compared before anything is told. The first connect is the page's own
  // load; every later one may have missed signals.
  let status = echo.connector.connectionStatus()
  let connectedBefore = status === 'connected'
  const stopWatching = echo.connector.onConnectionChange((next) => {
    if (next === status) return
    status = next
    // A socket that a sign-out already replaced speaks for no one.
    if (connection?.echo === echo) connected.value = next === 'connected'
    if (next !== 'connected') return
    if (connectedBefore) {
      for (const listener of [...reconnectListeners]) listener()
    }
    connectedBefore = true
  })

  return {
    echo,
    listeners: new Map(),
    close: () => {
      stopWatching()
      echo.disconnect()
    },
  }
}

function connect(): Promise<Connection | null> {
  if (building !== null) return building

  const config = liveConfig()
  if (config === null) return Promise.resolve(null)

  const startedIn = generation
  building = build(config).then(
    (built) => {
      // Signed out while the chunk was loading: this socket belongs to no one.
      if (startedIn !== generation) {
        built.close()
        return null
      }
      connection = built
      connected.value = built.echo.connector.connectionStatus() === 'connected'
      return built
    },
    (error: unknown) => {
      // A chunk that failed to load, or a client that refused its options.
      // The next caller may try again; the view keeps its polls meanwhile.
      if (startedIn === generation) building = null
      reportError(error)
      return null
    },
  )
  return building
}

/** The session's connection, or null when live updates are off. */
export async function getEcho(): Promise<LiveEcho | null> {
  return (await connect())?.echo ?? null
}

/**
 * Listens for one signal on a private channel. `channel` is the name without
 * the `private-` prefix, which Echo adds; `event` is the bare signal name.
 * Answers the way out, which is safe to call twice and after a sign-out.
 */
export async function subscribe(
  channel: string,
  event: string,
  handler: SignalHandler,
): Promise<() => void> {
  return listen(channel, event, () => handler())
}

/**
 * `subscribe` for the one channel whose events carry data: a remote session's
 * signalling (D138), which must reach the peer as it was sent. The payload is
 * untrusted, so the caller parses it before any use.
 */
export async function listen(
  channel: string,
  event: string,
  handler: (payload: unknown) => void,
): Promise<() => void> {
  const joined = await connect()
  if (joined === null) return () => {}

  // The leading dot tells Echo the name is exact, not a class under App.Events.
  const name = `.${event}`
  const listener = (payload: unknown): void => handler(payload)
  joined.echo.private(channel).listen(name, listener)
  joined.listeners.set(channel, (joined.listeners.get(channel) ?? 0) + 1)

  let done = false
  return () => {
    // A sign-out closed that connection and every channel with it.
    if (done || connection !== joined) return
    done = true
    joined.echo.private(channel).stopListening(name, listener)
    const left = (joined.listeners.get(channel) ?? 1) - 1
    if (left > 0) {
      joined.listeners.set(channel, left)
      return
    }
    joined.listeners.delete(channel)
    joined.echo.leave(channel)
  }
}

/** Calls back each time the socket connects again after a drop, never on the first connect. */
export function onReconnect(listener: SignalHandler): () => void {
  reconnectListeners.add(listener)
  return () => {
    reconnectListeners.delete(listener)
  }
}

/**
 * Closes the socket and forgets it, at sign-out. The next session builds its
 * own, so no channel of the earlier user survives into it.
 */
export function disconnectEcho(): void {
  generation += 1
  building = null
  connection?.close()
  connection = null
  connected.value = false
}
